import React, { useState, useEffect, useCallback } from 'react';
import { adminService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  FileText,
  Search,
  Trash2,
  ExternalLink,
} from 'lucide-react';

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.quotes({ search, page });
      if (res && res.data) {
        setQuotes(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar cotizaciones globales.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotes]);

  const handleDelete = async () => {
    if (!quoteToDelete) return;
    setDeleting(true);
    try {
      await adminService.deleteQuote(quoteToDelete.id);
      success('Cotización eliminada correctamente.');
      setDeleteConfirmOpen(false);
      setQuoteToDelete(null);
      fetchQuotes();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar cotización.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
          Cotizaciones Globales del Sistema
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Histórico y control de presupuestos emitidos por todos los usuarios
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por cotización o usuario..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando cotizaciones globales..." className="py-12" />
          ) : quotes.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No hay cotizaciones registradas"
              description="No se encontraron cotizaciones en la base de datos."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Emisor (Usuario)</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {quotes.map((q) => (
                    <tr
                      key={q.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                        #{q.number || String(q.id).padStart(6, '0')}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {q.user?.name || '—'}
                        </div>
                        <div className="text-[11px] text-slate-400">{q.user?.email}</div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {q.customer?.full_name || '—'}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {q.quote_date}
                      </td>

                      <td className="py-3.5 px-4 font-black text-slate-900 dark:text-slate-100">
                        {q.currency?.symbol || '$'} {Number(q.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {q.status === 1 ? (
                          <Badge variant="success" size="sm" dot>Convertida</Badge>
                        ) : (
                          <Badge variant="primary" size="sm" dot>Abierta</Badge>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={ExternalLink}
                            onClick={() => window.open(api.getPdfUrl('quotes', q.id), '_blank')}
                            title="Ver PDF"
                          />

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setQuoteToDelete(q);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Eliminar del sistema"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar cotización global?"
        message={`¿Estás seguro de que deseas eliminar la cotización #${quoteToDelete?.number}?`}
        confirmText="Eliminar"
        isLoading={deleting}
      />
    </div>
  );
};
